// The 5 anchor colors match the real SWMM GUI's default "Rainbow" legend
// scheme (Delphi TColor values, $BBGGRR order — not RGB), interpolated
// continuously here rather than that GUI's discrete 5-class legend.

interface Stop {
  ratio: number;
  r: number;
  g: number;
  b: number;
}

const STOPS: Stop[] = [
  { ratio: 0, r: 0x00, g: 0x00, b: 0xff },
  { ratio: 0.25, r: 0x00, g: 0xff, b: 0xff },
  { ratio: 0.5, r: 0x00, g: 0xff, b: 0x00 },
  { ratio: 0.75, r: 0xff, g: 0xff, b: 0x00 },
  { ratio: 1, r: 0xff, g: 0x00, b: 0x00 },
];

/** Clamps ratio to 0..1 so floating-point noise slightly past the exact peak still renders sensibly. */
export function heatColor(ratio: number): string {
  const t = Math.max(0, Math.min(1, ratio));
  let lo = STOPS[0];
  let hi = STOPS[STOPS.length - 1];
  for (let i = 0; i < STOPS.length - 1; i++) {
    if (t >= STOPS[i].ratio && t <= STOPS[i + 1].ratio) {
      lo = STOPS[i];
      hi = STOPS[i + 1];
      break;
    }
  }
  const span = hi.ratio - lo.ratio;
  const f = span > 0 ? (t - lo.ratio) / span : 0;
  const r = Math.round(lo.r + (hi.r - lo.r) * f);
  const g = Math.round(lo.g + (hi.g - lo.g) * f);
  const b = Math.round(lo.b + (hi.b - lo.b) * f);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Split out of buildElementColors so a caller re-coloring the same series on every animation frame can compute this once instead of rescanning every series on every frame. */
export function findSeriesPeak(series: Map<string, number[]>): number {
  let peak = 0;
  for (const values of series.values()) {
    for (const v of values) {
      const a = Math.abs(v);
      if (a > peak) peak = a;
    }
  }
  return peak;
}

/** Uses absolute value so a signed flow reads by magnitude regardless of direction. Returns an empty map when peak is zero/negative (a still-dry network, or no results yet). */
export function colorsAtTime(
  series: Map<string, number[]>,
  timeIndex: number,
  peak: number
): Map<string, string> {
  const colors = new Map<string, string>();
  if (peak <= 0) return colors;
  for (const [name, values] of series) {
    const v = values[timeIndex];
    if (v === undefined) continue;
    colors.set(name, heatColor(Math.abs(v) / peak));
  }
  return colors;
}

/** Normalizes against the global peak across every element and timestamp, not per-timestep, so the color scale means the same thing on every animation frame. Callers who re-color the same series on every frame should memoize findSeriesPeak separately instead of using this wrapper. */
export function buildElementColors(
  series: Map<string, number[]>,
  timeIndex: number
): Map<string, string> {
  return colorsAtTime(series, timeIndex, findSeriesPeak(series));
}
